import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { flushSync } from 'react-dom';
import { MuteButton, PauseButton } from '../VideoButton';
import { ProgressiveAssetController } from './ProgressiveAssetController';
import { ProgressiveAssetWrapper } from './ProgressiveAssetWrapper';
import { Video, VideoButtons } from './styles';
const VideoView = React.forwardRef(function VideoView({ ariaLabel, loading = 'eager', src, type = 'video/mp4', displayPauseButton, displayMuteButton, screenReaderStrings, autoPlay, ...rest }, forwardedRef) {
    const [isPaused, setIsPaused] = React.useState(!autoPlay);
    const [isMuted, setIsMuted] = React.useState(!!rest.muted);
    const toggleVideoPause = () => {
        if (isPaused) {
            videoRef.current?.play();
            setIsPaused(false);
        }
        else {
            videoRef.current?.pause();
            setIsPaused(true);
        }
    };
    const toggleVideoMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
        }
        setIsMuted(!isMuted);
    };
    const { completeLoading, hasLoaded } = ProgressiveAssetController.useContainer();
    const [computedSrc, setComputedSource] = React.useState(() => {
        return loading === 'defer' ? undefined : src;
    });
    const key = loading === 'defer' ? `defer-${src}` : src;
    const videoRef = React.useRef(null);
    const setVideoRef = React.useCallback((element) => {
        videoRef.current = element;
        if (typeof forwardedRef === 'function') {
            forwardedRef(element);
        }
        else if (forwardedRef) {
            ;
            forwardedRef.current = element;
        }
    }, [forwardedRef]);
    const isVideoReady = videoRef.current?.readyState === 4;
    React.useEffect(() => {
        if (loading !== 'defer' || computedSrc === src)
            return;
        const el = videoRef.current;
        if (!el)
            return;
        const observer = new IntersectionObserver(([entry]) => {
            if (!entry?.isIntersecting)
                return;
            observer.disconnect();
            flushSync(() => {
                setComputedSource(src);
            });
            el.load();
        }, { threshold: 0 });
        observer.observe(el);
        return () => observer.disconnect();
    }, [computedSrc, loading, src]);
    React.useEffect(() => {
        if (isVideoReady)
            completeLoading();
    }, [completeLoading, isVideoReady]);
    return (_jsxs(_Fragment, { children: [_jsx(Video, { "aria-label": ariaLabel, hasLoaded: hasLoaded, onCanPlay: completeLoading, ref: setVideoRef, autoPlay: autoPlay, ...rest, children: _jsx("source", { src: computedSrc, type: type }) }, key), _jsxs(VideoButtons, { children: [displayPauseButton && hasLoaded && (_jsx(PauseButton, { onClick: () => {
                            toggleVideoPause();
                        }, pauseText: screenReaderStrings?.pauseText ?? '', playText: screenReaderStrings?.playText ?? '', paused: isPaused, "aria-label": (isPaused ? screenReaderStrings?.playText : screenReaderStrings?.pauseText) ?? '' })), displayMuteButton && hasLoaded && (_jsx(MuteButton, { onClick: () => {
                            toggleVideoMute();
                        }, isMuted: isMuted, mutedText: screenReaderStrings?.muteText ?? '', notMutedText: screenReaderStrings?.unMuteText ?? '' }))] })] }));
});
export const ProgressiveAssetVideo = React.forwardRef(function ProgressiveAssetVideo({ aspectRatios, ...rest }, forwardedRef) {
    return (_jsx(ProgressiveAssetWrapper, { aspectRatios: aspectRatios, children: _jsx(VideoView, { ...rest, ref: forwardedRef }) }));
});
//# sourceMappingURL=ProgressiveAssetVideo.js.map